import { useEffect, useState } from 'react';
import { leadsApi, usersApi } from '../api';
import { useAuth } from '../hooks/useAuth';
import SearchableSelect from './SearchableSelect';

interface Props {
  lead?: any;
  onClose: () => void;
  onSave: () => void;
}

export default function LeadModal({ lead, onClose, onSave }: Props) {
  const { user } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [form, setForm] = useState({
    firstName: lead?.firstName || '',
    lastName: lead?.lastName || '',
    inn: lead?.inn || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    companyName: lead?.companyName || '',
    position: lead?.position || '',
    status: lead?.status || 'NEW',
    source: lead?.source || '',
    description: lead?.description || '',
    tags: lead?.tags?.join(', ') || '',
    assignedId: lead?.assignedId || '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!lead && user?.id && !form.assignedId) {
      setForm(prev => ({ ...prev, assignedId: user.id }));
    }
  }, [user]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await usersApi.list({ limit: 100 });
        setUsers(res.data.data || []);
      } catch {}
    };
    load();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...form,
        inn: form.inn || null,
        tags: form.tags.split(',').map((s: string) => s.trim()).filter(Boolean),
        assignedId: form.assignedId || null,
      };
      if (lead) {
        await leadsApi.update(lead.id, data);
      } else {
        await leadsApi.create(data);
      }
      onSave();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'NEW', label: 'Новый' },
    { value: 'IN_WORK', label: 'В работе' },
    { value: 'QUALIFIED', label: 'Квалифицирован' },
    { value: 'CONVERTED', label: 'Конвертирован' },
    { value: 'REJECTED', label: 'Отклонен' },
  ];

  const sourceOptions = ['Сайт', 'Звонок', 'Email', 'Соцсети', 'Рекомендация', 'Выставка', 'Другое'];

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{lead ? 'Редактировать лида' : 'Новый лид'}</h3>
          <button onClick={onClose} style={{ background: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Имя</label>
                <input className="form-input" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Фамилия</label>
                <input className="form-input" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">ИНН</label>
              <input className="form-input" value={form.inn} onChange={e => setForm({ ...form, inn: e.target.value })} placeholder="10 или 12 цифр" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Телефон</label>
                <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Компания</label>
                <input className="form-input" value={form.companyName} onChange={e => setForm({ ...form, companyName: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Должность</label>
                <input className="form-input" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Статус</label>
                <select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Источник</label>
                <select className="form-select" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                  <option value="">— Не выбрано —</option>
                  {sourceOptions.map((s: string) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <SearchableSelect
                label="Ответственный"
                value={form.assignedId}
                onChange={val => setForm({ ...form, assignedId: val })}
                options={users.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
                emptyLabel="— Не назначен —"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Описание</label>
              <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Теги (через запятую)</label>
              <input className="form-input" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Сохранение...' : 'Сохранить'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
